import {
  Alert,
  Amount,
  Box,
  Button,
  Card,
  CardBody,
  CheckIcon,
  DatePicker,
  DateValue,
  EditIcon,
  Radio,
  RadioGroup,
  Text,
  TextInput,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import useFetchCompensationDetails from 'components/PayDetails/hooks/useFetchCompensationDetails';
import { routePaths } from 'components/Routes/data';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import { format } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { FormEvent, useEffect, useReducer, useState } from 'react';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { AppError } from 'utils/AppError';
import { convertToOrdinal } from 'utils/Dates';
import { useWizardInfo } from '../providers/WizardInfoProvider';
import { SummaryView, SummaryViewItem } from './components/SummaryView';
import { revisePaySteps, ReviseSalaryFormData, reviseSalarySchema } from './constants';
import {
  checkIsCurrentPayrollMonth,
  createUpdatePayPayload,
  getArrearMonthsAlertText,
  getRevisionAlertMessageText,
  isDateAfterPayrollCycleStartDate,
} from './utils';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { UserFilters } from 'api/gql-utils/payDetails/types';
import { toIndianCurrencyString } from 'utils/Numbers';
import { INTEGER_AMOUNT } from 'utils/regexes';
import { customSalaryComponentsReducer } from './components/CustomSalaryComponents/reducer';
import {
  getInitialCustomComponents,
  validateCustomSalaryStructure,
} from './components/CustomSalaryComponents/utils';
import { CustomSalaryComponents } from './components/CustomSalaryComponents';
import { useSalaryStructureComponents } from 'components/SalaryStructures/creation/hooks/useSalaryStructureComponents';

const SalaryDetailsStep = () => {
  const history = useHistory();
  const { updateStepStatus } = useWizardInfo();
  const toast = useToast();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;
  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();
  const { validate, register, formData, setFieldValue } =
    useFormWithProvider<ReviseSalaryFormData>();

  const [pendingDateChange, setPendingDateChange] = useState<DateValue | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const payrollCycle = formData?.payrollSetupInfo?.payrollCycle!;

  const [isDateFieldDisabled, setIsDateFieldDisabled] = useState(true);

  const { isM3S1Enabled, isM3S3Enabled } = useIsFeatureDisabledForRearch();

  const { allNonAdhocComponents, isLoading: isFetchingComponents } = useSalaryStructureComponents({
    onSuccess: (data) => {
      dispatchCustomComponents({
        type: 'INITIALIZE_COMPONENTS',
        payload: {
          initialComponents: getInitialCustomComponents({
            allNonAdhocComponents: data.allNonAdhocComponents,
            exisitingCustomSalaryStructure: formData.customSalaryComponents ?? [],
            formData,
          }),
        },
      });
    },
    enabled: isM3S3Enabled,
  });

  // Initialize custom components state
  const [customComponents, dispatchCustomComponents] = useReducer(
    customSalaryComponentsReducer,
    formData.customSalaryComponents ??
      getInitialCustomComponents({
        allNonAdhocComponents: allNonAdhocComponents,
        exisitingCustomSalaryStructure: formData.customSalaryComponents ?? [],
        formData,
      }),
  );

  const selectedEffectiveDate = formData?.effectiveFrom!;

  const payrollMonth = format(selectedEffectiveDate, 'yyyy-MM-dd');

  const currentCompensation = formData.currentAnnualCtc;

  let userFilters: UserFilters | undefined = undefined;
  if (currentCompensation === 0) {
    userFilters = {
      includeZeroSalaryUsers: true,
    };
  }

  useEffect(() => {
    if (selectedEffectiveDate && userId) {
      getArrearApplicabilityDetails(format(selectedEffectiveDate, 'yyyy-MM-dd'));
    }
  }, []);

  const query = useFetchCompensationDetails({
    queryKey: ['getEmployeeCompensationData', userId, payrollMonth, formData.revisedCompensation],
    userId,
    payrollMonth,
    userFilters,
    userRevisions: [
      {
        revisionInfo: [
          {
            effectiveFromMonth: format(selectedEffectiveDate, 'yyyy-MM'),
            fullCtc: Number(formData.revisedCompensation!) * 100,
            proratedCtc: Number(formData.revisedCompensation!) * 100,
          },
        ],
      },
    ],
    enabled: false,
    onSuccess(data) {
      setFieldValue('ctcData', data.ctcBreakdownData);
      history.push({
        pathname: routePaths.payDetails.compensation.edit.review,
        search: `?userId=${userId}`,
      });
    },
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const payrollMonthMutation = useMutation({
    mutationFn: api.payrollQueries.getPayrollMonthFromDate,
  });

  const arrearApplicabilityMutation = useMutation({
    mutationFn: api.userQueries.getArrearApplicabilityDetails,
    mutationKey: ['getArrearApplicabilityDetails', userId],
  });

  const validateSalaryMutation = useMutation({
    mutationFn: api.compensationMutations.validatePay,
    onSuccess: () => query.refetch(),
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const isLoading =
    query.isLoading ||
    validateSalaryMutation.isLoading ||
    payrollMonthMutation.isLoading ||
    arrearApplicabilityMutation.isLoading;

  const arrearDetails =
    arrearApplicabilityMutation.isLoading || arrearApplicabilityMutation.error
      ? null
      : arrearApplicabilityMutation.data?.payrollGetArrearApplicabilityDetails?.data?.[0];

  const getArrearApplicabilityDetails = (effectiveDate?: string) => {
    if (isM3S1Enabled && effectiveDate && userId) {
      arrearApplicabilityMutation.mutate({
        input: {
          users: [
            {
              userId: parseInt(userId),
              effectiveDate,
            },
          ],
        },
      });
    }
  };

  const shouldNavigateToUpdateStructureStep = isM3S3Enabled
    ? !formData.isCustomSalaryStructure
    : isM3S1Enabled;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    updateStepStatus(revisePaySteps.step1, 'success');

    if (isM3S3Enabled && formData.isCustomSalaryStructure) {
      const errorMsg = validateCustomSalaryStructure(customComponents);
      if (errorMsg) {
        toast.show({
          content: errorMsg,
          color: 'negative',
          duration: 3000,
        });
        return;
      }
      setFieldValue('customSalaryComponents', customComponents);
      history.push({
        pathname: routePaths.payDetails.compensation.edit.review,
        search: `?userId=${userId}`,
      });
      return;
    }

    if (validate(reviseSalarySchema)) {
      const res = await payrollMonthMutation.mutateAsync({
        date: format(selectedEffectiveDate, 'yyyy-MM-dd'),
      });
      const effectivePayrollMonth = res.payrollGetMonthFromDate.data.payrollMonth;
      setFieldValue('effectivePayrollMonth', effectivePayrollMonth);

      if (shouldNavigateToUpdateStructureStep) {
        history.push({
          pathname: routePaths.payDetails.compensation.edit.updateStructure,
          search: `?userId=${userId}`,
        });
        return;
      }
      validateSalaryMutation.mutate(
        createUpdatePayPayload({
          userId: userId!,
          annualSalary: parseInt(formData.revisedCompensation ?? '0'),
          effectiveFrom: effectivePayrollMonth,
        }),
      );
    }
  };

  const isCurrentPayrollMonth = checkIsCurrentPayrollMonth(selectedEffectiveDate);
  const isAfterPayrollCycleStartDate = isDateAfterPayrollCycleStartDate(
    selectedEffectiveDate,
    payrollCycle,
  );

  const handleEditDateClick = () => {
    setIsDateFieldDisabled(!isDateFieldDisabled);
    setIsDatePickerOpen(true);
  };

  const { onChange: onChangeEffectiveFrom, ...restDatePickerProps } = register(
    'effectiveFrom',
    'date',
  );

  const {
    onChange: onChangeRevisedCompensation,
    value: revisedCompensationValue,
    ...restRevisedCompensationProps
  } = register('revisedCompensation');

  return (
    <>
      {/* TODO: This modal is planned for use in the upcoming release. */}
      {/* <FinalizedPayrollConflictModal
        isOpen={showFinalizedConflictModal}
        onClose={() => setShowFinalizedConflictModal(false)}
        conflictMonth={conflictPayrollMonth?.formatted || ''}
        payrollMonth={conflictPayrollMonth?.raw || ''}
      /> */}
      <FormWrapper onSubmit={handleSubmit}>
        <StepContentWrapper maxWidth="600px" noBgIllustration>
          <SummaryView>
            <SummaryViewItem label="Current CTC">
              <Amount size="large" value={formData.currentAnnualCtc!} suffix="none" />
            </SummaryViewItem>
            <SummaryViewItem label="Monthly CTC">
              <Amount size="large" value={formData.currentAnnualCtc! / 12} suffix="none" />
            </SummaryViewItem>
            <SummaryViewItem label="Effective from">
              <Text size="large">
                {format(new Date(formData.currentCtcEffectiveFrom!), 'dd MMM, yyyy')}
              </Text>
            </SummaryViewItem>
          </SummaryView>

          <Box display="flex" flexDirection="column" gap="spacing.6" marginTop="spacing.8">
            {isM3S3Enabled ? (
              <Box
                borderWidth="thin"
                borderColor="surface.border.gray.muted"
                borderRadius="medium"
                padding={'spacing.7'}>
                <StepHeader title="Choose" subtitle="Salary Structure" />
                <RadioGroup
                  name="salaryStructureType"
                  value={formData.isCustomSalaryStructure ? 'custom' : 'saved'}
                  onChange={({ value }) => {
                    setFieldValue('isCustomSalaryStructure', value === 'custom');
                  }}
                  label=""
                  marginTop="spacing.5">
                  <Card
                    as="label"
                    isSelected={!formData.isCustomSalaryStructure}
                    marginBottom="spacing.4"
                    accessibilityLabel="Apply a saved salary structure">
                    <CardBody>
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap="spacing.3"
                        alignItems="flex-start">
                        <Box display="flex" flexDirection="column" flex={1}>
                          <Text weight="semibold" size="medium">
                            Apply a saved salary structure
                          </Text>
                          <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                            Use a structure you've already created (previously, typically or for any
                            other employee).
                          </Text>
                        </Box>
                        <Radio value="saved" />
                      </Box>
                    </CardBody>
                  </Card>
                  <Card
                    as="label"
                    isSelected={formData.isCustomSalaryStructure}
                    accessibilityLabel="Edit custom structure">
                    <CardBody>
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap="spacing.3"
                        alignItems="flex-start">
                        <Box display="flex" flexDirection="column" flex={1}>
                          <Text weight="semibold" size="medium">
                            Edit custom structure
                          </Text>
                          <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                            Create the salary components and their amounts manually.
                          </Text>
                        </Box>
                        <Radio value="custom" />
                      </Box>
                    </CardBody>
                  </Card>
                </RadioGroup>
              </Box>
            ) : null}
            <Box
              borderWidth="thin"
              borderColor="surface.border.gray.muted"
              borderRadius="medium"
              padding={'spacing.7'}>
              {isM3S3Enabled && formData.isCustomSalaryStructure ? (
                <>
                  <StepHeader title="Custom" subtitle="Structure" />
                  <CustomSalaryComponents
                    selectedComponents={customComponents}
                    dispatch={dispatchCustomComponents}
                    isSubmitting={isLoading}
                    allComponents={allNonAdhocComponents}
                    isFetchingComponents={isFetchingComponents}
                  />
                </>
              ) : (
                <>
                  <StepHeader title="Revise" subtitle="Salary" />
                  <TextInput
                    value={toIndianCurrencyString(
                      revisedCompensationValue ? Number(revisedCompensationValue) : 0,
                      true,
                    )}
                    onChange={({ value, name }) => {
                      if (INTEGER_AMOUNT.test(value || '')) {
                        const numValue = Number(value?.replace(/,/g, '')) ?? 0;
                        onChangeRevisedCompensation?.({ name, value: numValue.toString() });
                      }
                    }}
                    {...restRevisedCompensationProps}
                    isDisabled={isLoading}
                    marginTop="spacing.5"
                    label="Enter the revised compensation"
                    prefix="₹"
                    placeholder="Enter the revised compensation"
                    size="medium"
                    autoFocus
                  />
                </>
              )}
            </Box>
            <Box
              borderWidth="thin"
              borderColor="surface.border.gray.muted"
              borderRadius="medium"
              padding={'spacing.7'}>
              <StepHeader title="Salary effective" subtitle="Date" />
              <Text as="p" size="medium" marginTop="spacing.5" color="surface.text.gray.muted">
                By default, the salary is effective from the{' '}
                <Text as="span" color="surface.text.gray.subtle">
                  {`${convertToOrdinal(payrollCycle.startDate)} of every month`}
                </Text>
                . To make it effective from mid-month (e.g., 16th), specify the date.
              </Text>
              <Box display="flex" alignItems="center" marginTop="spacing.3" gap="spacing.4">
                <DatePicker
                  label=""
                  accessibilityLabel="Effective Date"
                  {...restDatePickerProps}
                  onChange={(data) => {
                    onChangeEffectiveFrom?.(data);
                    setPendingDateChange(data);
                  }}
                  isDisabled={isDateFieldDisabled || isLoading}
                  onOpenChange={({ isOpen }) => {
                    setIsDatePickerOpen(isOpen);
                    if (!isOpen && pendingDateChange) {
                      getArrearApplicabilityDetails(
                        format(pendingDateChange ?? new Date(), 'yyyy-MM-dd'),
                      );
                      setPendingDateChange(null);
                    }
                  }}
                  isOpen={isDatePickerOpen}
                />
                <Button
                  isDisabled={isLoading}
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={handleEditDateClick}>
                  {isDateFieldDisabled ? 'Edit' : 'Done'}
                </Button>
              </Box>
              {isM3S1Enabled && arrearDetails && arrearDetails.arrearApplicableMonths.length > 0 ? (
                <Alert
                  marginTop="spacing.7"
                  icon={CheckIcon}
                  color="positive"
                  description={getArrearMonthsAlertText(arrearDetails)}
                />
              ) : isAfterPayrollCycleStartDate && isCurrentPayrollMonth ? (
                <Alert
                  marginTop="spacing.7"
                  icon={CheckIcon}
                  color="positive"
                  description={getRevisionAlertMessageText(payrollCycle, selectedEffectiveDate)}
                />
              ) : null}
            </Box>
          </Box>
        </StepContentWrapper>
        <StepActionsWrapper maxWidth="600px">
          <span />
          <Button type="submit" isLoading={isLoading}>
            Next
          </Button>
        </StepActionsWrapper>
      </FormWrapper>
    </>
  );
};

export default SalaryDetailsStep;
